// Analytics utility for production tracking with enhanced traffic source tracking
class Analytics {
  constructor() {
    this.isEnabled = process.env.REACT_APP_ENABLE_ANALYTICS === 'true' && process.env.NODE_ENV === 'production';
    this.isDebug = process.env.NODE_ENV === 'development';
    this.mainDomain = process.env.REACT_APP_MAIN_DOMAIN || 'lingua-leap.org';

    // Initialize traffic tracking
    this.trafficSource = null;
    this.utmParams = {};
    this.firstTouch = null;

    if (this.isEnabled) {
      this.captureTrafficSource();
      this.initializeGA();
      this.initializeClarity();
    }
  }

  // Capture and classify traffic source
  captureTrafficSource() {
    // Capture UTM parameters from URL
    this.utmParams = this.getUTMParams();

    // Get referrer information
    const referrer = document.referrer;
    const hostname = referrer ? new URL(referrer).hostname : '';

    // Classify traffic source
    this.trafficSource = this.classifyTrafficSource(referrer, hostname, this.utmParams);

    // Store in session storage for persistence across pages
    sessionStorage.setItem('traffic_source', this.trafficSource);
    sessionStorage.setItem('utm_params', JSON.stringify(this.utmParams));
    sessionStorage.setItem('referrer', referrer);

    // First-touch attribution (stored in localStorage for long-term tracking)
    if (!localStorage.getItem('first_traffic_source')) {
      localStorage.setItem('first_traffic_source', this.trafficSource);
      localStorage.setItem('first_utm_params', JSON.stringify(this.utmParams));
      localStorage.setItem('first_visit_date', new Date().toISOString());
    }

    this.firstTouch = {
      source: localStorage.getItem('first_traffic_source'),
      utm: JSON.parse(localStorage.getItem('first_utm_params') || '{}'),
      date: localStorage.getItem('first_visit_date')
    };

    if (this.isDebug) {
      console.log('Traffic Source Tracking:', {
        current: this.trafficSource,
        utmParams: this.utmParams,
        firstTouch: this.firstTouch,
        referrer: referrer
      });
    }
  }

  // Extract UTM parameters from URL
  getUTMParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      content: params.get('utm_content') || '',
      term: params.get('utm_term') || ''
    };
  }

  // Classify traffic source based on referrer and UTM params
  classifyTrafficSource(referrer, hostname, utmParams) {
    // UTM parameters take priority
    if (utmParams.source) {
      const medium = utmParams.medium || 'unknown';
      return `${utmParams.source} - ${medium}`;
    }

    // No referrer = Direct traffic
    if (!referrer) {
      return 'Direct';
    }

    // Same domain = Internal
    if (hostname.includes(window.location.hostname)) {
      return 'Internal';
    }

    // Search engines
    const searchEngines = {
      'google.': 'Organic Search - Google',
      'bing.': 'Organic Search - Bing',
      'yahoo.': 'Organic Search - Yahoo',
      'duckduckgo.': 'Organic Search - DuckDuckGo',
      'baidu.': 'Organic Search - Baidu',
      'yandex.': 'Organic Search - Yandex',
      'ecosia.': 'Organic Search - Ecosia'
    };

    for (const [engine, label] of Object.entries(searchEngines)) {
      if (hostname.includes(engine)) return label;
    }

    // Social media platforms
    const socialPlatforms = {
      'facebook.com': 'Social - Facebook',
      'fb.com': 'Social - Facebook',
      'twitter.com': 'Social - Twitter',
      'x.com': 'Social - X/Twitter',
      'linkedin.com': 'Social - LinkedIn',
      'instagram.com': 'Social - Instagram',
      'tiktok.com': 'Social - TikTok',
      'youtube.com': 'Social - YouTube',
      'reddit.com': 'Social - Reddit',
      'pinterest.com': 'Social - Pinterest',
      'snapchat.com': 'Social - Snapchat',
      'whatsapp.com': 'Social - WhatsApp',
      'telegram.': 'Social - Telegram'
    };

    for (const [platform, label] of Object.entries(socialPlatforms)) {
      if (hostname.includes(platform)) return label;
    }

    // Email clients (if detectable)
    if (hostname.includes('mail.') || hostname.includes('outlook.') || hostname.includes('gmail.')) {
      return 'Email';
    }

    // Everything else is referral traffic
    return `Referral - ${hostname}`;
  }

  initializeGA() {
    const trackingId = process.env.REACT_APP_GA_TRACKING_ID;
    if (!trackingId) return;

    // Google Analytics 4
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;

    gtag('js', new Date());

    // Configure with cross-domain tracking
    gtag('config', trackingId, {
      page_title: document.title,
      page_location: window.location.href,
      linker: {
        domains: [this.mainDomain, `marketing.${this.mainDomain}`]
      },
      // Add default custom dimensions
      custom_map: {
        dimension1: 'traffic_source',
        dimension2: 'user_type'
      }
    });
  }

  initializeClarity() {
    const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
    if (!projectId) return;

    // Microsoft Clarity
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", projectId);
  }

  // Get enhanced event context
  getEventContext() {
    const isReturning = localStorage.getItem('has_visited') === 'true';
    const userType = isReturning ? 'Returning' : 'New';

    // Mark as visited
    if (!isReturning) {
      localStorage.setItem('has_visited', 'true');
    }

    return {
      domain: window.location.hostname,
      traffic_source: sessionStorage.getItem('traffic_source') || this.trafficSource || 'Unknown',
      referrer: sessionStorage.getItem('referrer') || document.referrer || 'Direct',
      user_type: userType,
      utm_source: this.utmParams.source || '',
      utm_medium: this.utmParams.medium || '',
      utm_campaign: this.utmParams.campaign || '',
      utm_content: this.utmParams.content || '',
      utm_term: this.utmParams.term || '',
      first_traffic_source: this.firstTouch?.source || '',
      first_visit_date: this.firstTouch?.date || ''
    };
  }

  // Track page views with enhanced context
  trackPageView(path, title) {
    const context = this.getEventContext();

    if (!this.isEnabled) {
      if (this.isDebug) {
        console.log('Analytics (Debug) - Page View:', path, title, context);
      }
      return;
    }

    if (window.gtag) {
      window.gtag('config', process.env.REACT_APP_GA_TRACKING_ID, {
        page_path: path,
        page_title: title
      });

      // Send page_view event with enhanced context
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title,
        ...context
      });
    }
  }

  // Track events with enhanced context
  trackEvent(action, category = 'User Interaction', label = '', value = 0) {
    const context = this.getEventContext();

    if (!this.isEnabled) {
      if (this.isDebug) {
        console.log('Analytics (Debug) - Event:', { action, category, label, value, ...context });
      }
      return;
    }

    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        ...context
      });
    }
  }

  // Track CTA clicks
  trackCTA(ctaName, location) {
    this.trackEvent('cta_click', 'CTA', `${ctaName} - ${location}`);
  }

  // Track form submissions
  trackFormSubmit(formName) {
    this.trackEvent('form_submit', 'Form', formName);
  }

  // Track video interactions
  trackVideoEvent(action, videoTitle) {
    this.trackEvent(action, 'Video', videoTitle);
  }

  // Track scroll depth
  trackScrollDepth(percentage) {
    if (percentage % 25 === 0) { // Track at 25%, 50%, 75%, 100%
      this.trackEvent('scroll_depth', 'Engagement', `${percentage}%`);
    }
  }

  // Track social media clicks
  trackSocialClick(platform, location) {
    this.trackEvent('social_click', 'Social Media', `${platform} - ${location}`);
  }

  // Track performance metrics
  trackPerformance() {
    if (!this.isEnabled || !window.performance) return;

    setTimeout(() => {
      const perfData = performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;

      if (pageLoadTime > 0) {
        this.trackEvent('page_load_time', 'Performance', 'Load Time', pageLoadTime);
      }
    }, 0);
  }

  // Auto-decorate links to main domain for cross-domain tracking
  decorateCrossDomainLinks() {
    if (!this.isEnabled) return;

    // Find all links to the main domain
    const links = document.querySelectorAll(`a[href*="${this.mainDomain}"]`);

    links.forEach(link => {
      // Skip if already decorated or is a relative link
      if (link.href.includes('_gl=') || !link.href.startsWith('http')) return;

      // Add click listener to append tracking parameters
      link.addEventListener('click', (e) => {
        // Let gtag handle the cross-domain linking automatically
        // This ensures the GA client ID is preserved
        if (window.gtag) {
          const url = new URL(link.href);

          // Add UTM parameters if they exist in current session
          if (this.utmParams.source) {
            url.searchParams.set('utm_source', this.utmParams.source);
            url.searchParams.set('utm_medium', this.utmParams.medium || 'referral');
            url.searchParams.set('utm_campaign', this.utmParams.campaign || 'marketing-site');
          }

          // Track the cross-domain navigation
          this.trackEvent('cross_domain_click', 'Navigation', `To ${this.mainDomain}`);
        }
      });
    });

    if (this.isDebug) {
      console.log(`Decorated ${links.length} cross-domain links to ${this.mainDomain}`);
    }
  }

  // Initialize cross-domain tracking (call after DOM is ready)
  initCrossDomainTracking() {
    this.decorateCrossDomainLinks();

    // Re-decorate on route changes (for SPAs)
    if (window.MutationObserver) {
      const observer = new MutationObserver(() => {
        this.decorateCrossDomainLinks();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }
}

// Create singleton instance
const analytics = new Analytics();

// Export for use throughout the app
export default analytics;

// Convenience functions
export const trackPageView = (path, title) => analytics.trackPageView(path, title);
export const trackEvent = (action, category, label, value) => analytics.trackEvent(action, category, label, value);
export const trackCTA = (ctaName, location) => analytics.trackCTA(ctaName, location);
export const trackSocialClick = (platform, location) => analytics.trackSocialClick(platform, location);
export const initCrossDomainTracking = () => analytics.initCrossDomainTracking();